import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Settings,
  BarChart3,
  LogOut,
  User,
  Menu,
  X,
  CreditCard,
  ChevronDown,
  Users,
  Headset,
  Palette,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Dices,
  Bell,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
// import { useTour } from '../../contexts/TourContext';
import { useState, useRef, useEffect } from 'react';
import { useUnviewedLeadsCount } from '../../hooks/useUnviewedLeads';
import FreePlanPaywall from '../../components/FreePlanPaywall';
import InstallAppButton from '../../components/InstallAppButton';
import NotificationBell from '../../components/NotificationBell';
import { KIWIFY_PRODUCT_URL, KIWIFY_PRODUCT_URL_ANNUAL } from '../../constants';

const whatsappNumber = '5553981670502';
const whatsappMessage = encodeURIComponent('Olá! Gostaria de ajuda sobre a Roda de Prêmios.');
const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

interface MenuItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  exact?: boolean;
  permission: string;
  dataTour: string;
  isFreePlan: boolean;
  badge?: number;
}

interface MenuSection {
  label: string | null;
  items: MenuItem[];
}

const AdminLayout: React.FC = () => {
  const { user, isFreePlan, logout, canAccess, isAdmin } = useAuth();
  // const { resetTour } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isBannerMinimized, setIsBannerMinimized] = useState(() => {
    return localStorage.getItem('freePlanBannerMinimized') === 'true';
  });
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { count: unviewedLeadsCount } = useUnviewedLeadsCount();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Menu organizado em seções com rótulos vintage. Mantém ordem original dos itens.
  const menuSections: MenuSection[] = [
    {
      label: null,
      items: [
        {
          path: '/admin',
          icon: LayoutDashboard,
          label: 'Dashboard',
          exact: true,
          permission: 'view_dashboard',
          dataTour: 'dashboard',
          isFreePlan: true,
        },
      ],
    },
    {
      label: 'Operação',
      items: [
        {
          path: '/admin/products',
          icon: Package,
          label: 'Produtos',
          permission: 'view_products',
          dataTour: 'products',
          isFreePlan: true,
        },
        {
          path: '/admin/wheels',
          icon: Settings,
          label: 'Sorteios',
          permission: 'view_wheels',
          dataTour: 'wheels',
          isFreePlan: true,
        },
        {
          path: '/admin/manual-draw',
          icon: Dices,
          label: 'Sorteio Manual',
          permission: 'view_wheels',
          dataTour: 'manual-draw',
          isFreePlan: false,
        },
        {
          path: '/admin/themes',
          icon: Palette,
          label: 'Temas',
          permission: 'view_wheels',
          dataTour: 'themes',
          isFreePlan: false,
        },
      ],
    },
    {
      label: 'Audiência',
      items: [
        {
          path: '/admin/leads',
          icon: User,
          label: 'Clientes',
          permission: 'view_spins',
          dataTour: 'leads',
          isFreePlan: true,
          badge: unviewedLeadsCount > 0 ? unviewedLeadsCount : undefined,
        },
        {
          path: '/admin/whatsapp',
          icon: MessageSquare,
          label: 'WhatsApp',
          permission: 'view_spins',
          dataTour: 'whatsapp',
          isFreePlan: false,
        },
        {
          path: '/admin/reports',
          icon: BarChart3,
          label: 'Relatórios',
          permission: 'view_dashboard',
          dataTour: 'reports',
          isFreePlan: true,
        },
        {
          path: '/admin/notifications',
          icon: Bell,
          label: 'Notificações',
          permission: 'view_dashboard',
          dataTour: 'notifications',
          isFreePlan: true,
        },
      ],
    },
    {
      label: 'Conta',
      items: [
        {
          path: '/admin/subscription',
          icon: CreditCard,
          label: 'Assinatura',
          permission: 'view_dashboard',
          dataTour: 'subscription',
          isFreePlan: true,
        },
        ...(isAdmin
          ? [{
              path: '/admin/users',
              icon: Users,
              label: 'Usuários',
              permission: 'manage_users',
              dataTour: 'users',
              isFreePlan: true,
            }]
          : []),
      ],
    },
  ];

  // Filtra itens por permissão e plano gratuito; mantém só seções com pelo menos um item visível.
  const visibleSections = menuSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (!canAccess(item.permission)) return false;
        if (item.isFreePlan === false && isFreePlan) return false;
        return true;
      }),
    }))
    .filter(section => section.items.length > 0);

  const isActiveRoute = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Bloquear acesso de planos gratuitos a partir de 13/04/2026
  const freePlanCutoffDate = new Date('2026-04-13T00:00:00-03:00');
  const isAfterCutoff = new Date() >= freePlanCutoffDate;

  if (isFreePlan && isAfterCutoff) {
    return (
      <FreePlanPaywall />
    )
  }

  const handleMinimizeBanner = () => {
    setIsBannerMinimized(true);
    localStorage.setItem('freePlanBannerMinimized', 'true');
  };

  const handleExpandBanner = () => {
    setIsBannerMinimized(false);
    localStorage.removeItem('freePlanBannerMinimized');
  };

  return (
    <div className="min-h-screen bg-creme-light font-body">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-tinta/60 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile floating menu trigger (shown only when sidebar is closed) */}
      {!isSidebarOpen && (
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="fixed top-4 left-4 z-20 p-2 rounded-lg bg-white border border-tinta/15 shadow-sm text-tinta-soft hover:text-bordo hover:bg-creme-light lg:hidden"
          aria-label="Abrir menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      <NotificationBell />

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-30 bg-white border-r border-tinta/10 transform transition-all duration-300 ease-in-out flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isSidebarExpanded ? 'w-64' : 'w-20'
        } lg:translate-x-0`}>
        {/* Desktop collapse toggle — sits half-outside the sidebar's right edge */}
        <button
          onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 items-center justify-center rounded-full bg-white border border-tinta/20 shadow-sm hover:border-bordo hover:text-bordo text-tinta-soft transition-colors z-10"
          title={isSidebarExpanded ? 'Retrair menu' : 'Expandir menu'}
          aria-label={isSidebarExpanded ? 'Retrair menu' : 'Expandir menu'}
        >
          {isSidebarExpanded ? <ChevronLeft className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>

        <div className="flex items-center justify-between h-16 px-6 border-b border-tinta/10 flex-shrink-0">
          {isSidebarExpanded && <img src='/roda_de_premios_horizontal.svg' alt="Roda de Prêmios" className='h-10' />}
          {!isSidebarExpanded && (
            <div className="w-full flex justify-center">
              <img src='/logo.svg' alt="Roda de Prêmios" className='h-10' />
            </div>
          )}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-tinta-soft hover:text-bordo hover:bg-creme-light"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto pt-4 pb-3 px-3">
          {visibleSections.map((section, sectionIdx) => (
            <div key={section.label ?? `section-${sectionIdx}`} className={sectionIdx > 0 ? 'mt-5' : ''}>
              {isSidebarExpanded && section.label && (
                <div className="px-3 mb-2 flex items-center gap-2">
                  <span className="font-display italic text-[11px] uppercase tracking-[0.22em] text-tinta-soft/65 font-semibold">
                    {section.label}
                  </span>
                  <span className="flex-1 h-px bg-tinta/10" />
                </div>
              )}
              {!isSidebarExpanded && section.label && sectionIdx > 0 && (
                <div className="mx-3 mb-2 h-px bg-tinta/10" />
              )}
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const Icon = item.icon;
                  const isActive = isActiveRoute(item.path, item.exact);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsSidebarOpen(false)}
                      data-tour={item.dataTour}
                      title={!isSidebarExpanded ? item.label : ''}
                      className={`group flex items-center ${isSidebarExpanded ? 'px-3' : 'px-0 justify-center'} py-2.5 text-sm rounded-lg transition-all ${
                        isActive
                          ? 'bg-bordo text-creme font-bold shadow-[2px_2px_0_0_#1A1A1A]'
                          : 'text-tinta-soft font-medium hover:text-bordo hover:bg-creme-dark/40'
                      }`}
                    >
                      <div className="relative flex items-center">
                        <Icon className={`${isSidebarExpanded ? 'mr-3' : ''} h-5 w-5 ${isActive ? 'text-creme' : 'text-tinta-soft/70 group-hover:text-bordo'}`} />
                        {isSidebarExpanded && item.badge && item.badge > 0 && (
                          <span className={`absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                            isActive ? 'bg-mostarda text-tinta' : 'bg-bordo text-creme'
                          }`}>
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </div>
                      {isSidebarExpanded && (
                        <span className="flex-1">{item.label}</span>
                      )}
                      {isSidebarExpanded && item.badge && item.badge > 0 && (
                        <span className={`ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                          isActive ? 'bg-mostarda text-tinta' : 'bg-bordo text-creme'
                        }`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Install app + Support CTA — separated from nav, before user info */}
        <div className="px-3 py-3 border-t border-tinta/10 flex-shrink-0 space-y-2">
          <InstallAppButton isExpanded={isSidebarExpanded} onAction={() => setIsSidebarOpen(false)} />
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            title={!isSidebarExpanded ? 'Falar com Suporte' : ''}
            className={`group flex items-center ${isSidebarExpanded ? 'gap-3 px-3' : 'justify-center px-0'} py-2.5 text-sm rounded-lg border border-mostarda/40 bg-mostarda/15 text-tinta hover:bg-mostarda/30 hover:border-mostarda transition-colors`}
          >
            <Headset className="h-5 w-5 text-mostarda-dark flex-shrink-0" />
            {isSidebarExpanded && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-tinta leading-tight">Falar com Suporte</p>
                <p className="text-[11px] text-tinta-soft italic leading-tight">Resposta no WhatsApp</p>
              </div>
            )}
          </a>
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-t border-tinta/10 flex-shrink-0">
          <div className="relative" ref={userMenuRef}>
            {isSidebarExpanded ? (
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-tour="user-menu"
                className="flex items-center w-full hover:bg-creme-light rounded-lg p-2 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-full bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                    <User className="h-5 w-5 text-bordo" />
                  </div>
                </div>
                <div className="ml-3 flex-1 text-left w-20">
                  <p className="text-sm font-bold text-tinta truncate">{user?.name}</p>
                  <p className="text-xs text-tinta-soft capitalize italic">
                    {user?.role === 'owner' ? 'Proprietário' :
                      user?.role === 'admin' ? 'Administrador' : 'Usuário'}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 text-tinta-soft transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
            ) : (
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-tour="user-menu"
                className="flex items-center justify-center w-full hover:bg-creme-light rounded-lg p-2 transition-colors"
                title={user?.name}
              >
                <div className="h-9 w-9 rounded-full bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                  <User className="h-5 w-5 text-bordo" />
                </div>
              </button>
            )}

            {/* User dropdown menu */}
            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-tinta/10 py-1 w-min">
                <Link
                  to="/admin/profile"
                  onClick={() => {
                    setShowUserMenu(false);
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm text-tinta-soft hover:bg-creme-light hover:text-bordo"
                >
                  <User className="h-4 w-4 mr-3 text-tinta-soft/70" />
                  Meu Perfil
                </Link>

                <Link
                  to="/admin/settings"
                  onClick={() => {
                    setShowUserMenu(false);
                    setIsSidebarOpen(false);
                  }}
                  data-tour="settings"
                  className="flex items-center px-4 py-2 text-sm text-tinta-soft hover:bg-creme-light hover:text-bordo"
                >
                  <Settings className="h-4 w-4 mr-3 text-tinta-soft/70" />
                  Configurações
                </Link>

                <hr className="my-1 border-tinta/10" />
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-bordo hover:bg-bordo/8"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${isSidebarExpanded ? 'lg:pl-64' : 'lg:pl-20'}`}>
        {/* Page content — pt-16 on mobile to clear the floating menu trigger */}
        <main className="flex-1 pt-16 lg:pt-0">

          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Banner de descontinuação do plano gratuito */}
              {isFreePlan && !isAfterCutoff && (
                isBannerMinimized ? (
                  <div className="mb-6 bg-mostarda/15 border border-mostarda/40 rounded-lg px-4 py-2.5 shadow-sm flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertTriangle className="w-4 h-4 text-mostarda-dark flex-shrink-0" />
                      <span className="text-sm text-tinta font-semibold truncate">
                        Plano gratuito será desativado em 13/04/2026
                      </span>
                    </div>
                    <button
                      onClick={handleExpandBanner}
                      className="text-xs text-bordo hover:text-bordo-dark underline whitespace-nowrap flex-shrink-0 font-semibold"
                    >
                      Ver detalhes
                    </button>
                  </div>
                ) : (
                  <div className="mb-6 bg-mostarda/15 border border-mostarda/40 rounded-xl p-4 sm:p-5 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-mostarda/25 rounded-lg flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-mostarda-dark" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display text-base font-bold text-tinta mb-1">
                          O plano gratuito será desativado em 13 de abril de 2026
                        </h4>
                        <p className="text-sm text-tinta-soft mb-3">
                          A partir desta data, será necessário ter um plano ativo para continuar utilizando a plataforma.
                          Até lá, você pode <strong className="text-tinta">exportar seus dados normalmente</strong> na seção de Relatórios e Clientes.
                        </p>
                        <div className="flex items-center gap-2 mb-3 text-xs text-tinta-soft">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>Você tem até <strong className="text-tinta">13/04/2026</strong> para fazer o upgrade</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <a
                            href={KIWIFY_PRODUCT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-bordo hover:bg-bordo-dark text-creme text-sm font-bold rounded-lg transition-colors"
                          >
                            Mensal — R$ 9,90
                          </a>
                          <a
                            href={KIWIFY_PRODUCT_URL_ANNUAL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-musgo hover:bg-musgo-dark text-creme text-sm font-bold rounded-lg transition-colors"
                          >
                            Anual — R$ 99,00
                          </a>
                          <button
                            onClick={handleMinimizeBanner}
                            className="ml-auto text-xs text-tinta-soft hover:text-bordo underline transition-colors"
                          >
                            Não mostrar novamente
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
